import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {EDIT_RECTANGLE_FEATURE_ID} from 'app/components/DeclarativeMap/DrawLayer';
import {OrderImageryState} from 'app/components/OrderImagerySidebar';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {useFeatureDataForFeature} from 'app/providers/FeatureDataProvider';
import {OrderableScenesLoader} from 'app/providers/OrderableScenesProvider';
import * as featureUtils from 'app/utils/featureUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as layers from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';

import {MapStateDispatch} from './MapStateProvider';

/**
 * Loads scene  from the url when present. After the first render where featureData
 * exists, it is removed from the url. Since we do not have the orderableScenes on
 * this first rendering the monitorProjectView, this component functions to keep track
 * of what scene needs to be loaded and wait until we have orderableScenes
 * to load it. After it is loaded the component state is set back to null.
 */
const UrlSceneHandler: React.FunctionComponent<
  React.PropsWithChildren<{
    imageRefFromUrl: mapUtils.MapImageRef | null;
    subsetFeatureBboxFromUrl: string | undefined;
    mapStateDispatch: MapStateDispatch;
    orderableScenesLoader: OrderableScenesLoader;
    selectedFeature: I.ImmutableOf<ApiFeature> | null;
    openOrderImageryModal: (sceneToOrder: I.ImmutableOf<ApiOrderableScene>) => void;
    orderImageryState: OrderImageryState;
    setOrderImageryState: (orderImageryState: OrderImageryState) => void;
  }>
> = ({
  imageRefFromUrl,
  subsetFeatureBboxFromUrl,
  mapStateDispatch,
  orderableScenesLoader,
  selectedFeature,
  openOrderImageryModal,
  orderImageryState,
  setOrderImageryState,
}) => {
  const featureData = useFeatureDataForFeature(selectedFeature?.get('id'));
  // we store the imageRef (and partial order geometry if present) from the
  // URL in state because these values drop from the URL during page load
  const [orderableSceneImageRef, setOrderableSceneImageRef] =
    React.useState<mapUtils.MapImageRef | null>(null);
  const [subsetFeature, setSubsetFeature] = React.useState<geojson.Feature<geojson.Polygon> | null>(
    null
  );

  // The link already applied. Its params stay in the URL until a router
  // transition strips them, and re-applying the link meanwhile (featureData
  // refreshing, say) would undo whatever the user did on the map since.
  const handledImageRefRef = React.useRef<mapUtils.MapImageRef | null>(null);

  /*
   * This should only happen once - when a scene is set in the url and featureData has loaded.
   * If it is unordered we update the state with that cursor, otherwise we set the map state to it.
   */
  React.useEffect(() => {
    if (!imageRefFromUrl || !featureData || handledImageRefRef.current === imageRefFromUrl) {
      return;
    }

    // reconstruct and store the partial order geometry based on the bbox string from the URL
    if (subsetFeatureBboxFromUrl) {
      setSubsetFeature(
        geoJsonUtils.bboxPolygon(JSON.parse(subsetFeatureBboxFromUrl), {
          id: EDIT_RECTANGLE_FEATURE_ID,
        })
      );
    }

    const featureDatum = featureData.find((d) => d!.get('date') === imageRefFromUrl.cursor);

    if (featureDatum) {
      handledImageRefRef.current = imageRefFromUrl;
      // Lack of template URL is a sign that the image hasn’t been ordered,
      // though it may be processing. (In either case we can’t show it on the
      // map, so we check the scene API rather than jumping to it with
      // setMapState.)
      if (!featureUtils.getHasTemplateUrl(featureDatum, imageRefFromUrl.layerKey)) {
        setOrderableSceneImageRef(imageRefFromUrl);
      } else {
        mapStateDispatch({
          type: 'SET_IMAGE_REFS',
          imageRefs: [imageRefFromUrl],
        });

        setOrderableSceneImageRef(null);
      }
    }
  }, [
    featureData,
    imageRefFromUrl,
    selectedFeature,
    mapStateDispatch,
    orderableScenesLoader,
    subsetFeatureBboxFromUrl,
  ]);

  const orderableScenesStatus =
    !!selectedFeature?.get('id') &&
    !!orderableSceneImageRef &&
    orderableScenesLoader(selectedFeature.get('id'));
  const orderableScenes =
    orderableScenesStatus && orderableScenesStatus.status === 'some'
      ? orderableScenesStatus.value
      : undefined;

  /*
   * This also should only happen once. If the scene is unordered, after the cursor is set
   * in state we fetch the orderableScenes. Once we have the scene we set the modal to
   * open that scene and set the cursor state back to null so this will not happen again.
   */
  React.useEffect(() => {
    if (orderableSceneImageRef && orderableScenes) {
      if (layerUtils.isLayerKeyHighResTruecolor(orderableSceneImageRef.layerKey)) {
        // We need to support legacy URLs that don’t have a source-specific
        // layerKey.
        const sourceIdPredicate =
          orderableSceneImageRef.layerKey === layers.ANY_TRUECOLOR_HIGH_RES
            ? () => true
            : (sourceId: string) =>
                sourceId === featureUtils.sourceIdFromLayerKey(orderableSceneImageRef.layerKey);

        const sceneToOpen = orderableScenes.find(
          (scene) =>
            scene!.get('sensingTime') === orderableSceneImageRef.cursor &&
            sourceIdPredicate(scene!.get('sourceId'))
        );

        // update orderImageryState to include the partial order geom from the URL
        if (subsetFeature && sceneToOpen) {
          setOrderImageryState({
            ...orderImageryState,
            subsetFeature: subsetFeature,
            orderSubset: true,
          });
        }
        // TODO(fiona): Do we need a specific experience around scenes that are
        // pending processing?
        if (sceneToOpen && !sceneToOpen.get('isOrdered')) {
          openOrderImageryModal(sceneToOpen);
        }
      }

      setOrderableSceneImageRef(null);
    }
  }, [
    orderableScenes,
    orderableSceneImageRef,
    openOrderImageryModal,
    subsetFeatureBboxFromUrl,
    setOrderImageryState,
    orderImageryState,
    subsetFeature,
  ]);

  return null;
};

export default UrlSceneHandler;
